import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Sparkles, AlertTriangle, TrendingUp, DollarSign, Activity, RefreshCw } from 'lucide-react';

function formatEUR(value) {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(value);
}

export default function AIUsage() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/ai/usage');
      setOverview(data);
    } catch (e) {
      console.error('[ai-usage] load failed', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-4" data-testid="page-ai-usage">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Sparkles size={18} className="text-brand-amber" /> AI-gebruik & kosten
        </h2>
        <button onClick={load} className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
          <RefreshCw size={14} /> Vernieuwen
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Laden…</p>
      ) : !overview ? (
        <p className="text-sm text-slate-500">Geen data</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Card icon={Activity} label="Calls deze maand" value={overview.total_calls} sublabel={overview.year_month} />
            <Card icon={DollarSign} label="Totaalkosten EUR" value={formatEUR(overview.total_cost_eur)} sublabel="Alle locaties" />
            <Card icon={TrendingUp} label="Locaties met AI-activiteit" value={overview.per_location?.length || 0} />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Locatie</th>
                  <th className="px-4 py-2 text-right">Calls</th>
                  <th className="px-4 py-2 text-right">Input tokens</th>
                  <th className="px-4 py-2 text-right">Output tokens</th>
                  <th className="px-4 py-2 text-right">Kosten</th>
                </tr>
              </thead>
              <tbody>
                {(overview.per_location || []).length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">Nog geen AI-gebruik deze maand</td></tr>
                ) : overview.per_location.map((r) => (
                  <tr key={r.location_id} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-medium text-slate-900">{r.location_id}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.calls_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">{r.tokens_input.toLocaleString('nl-NL')}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">{r.tokens_output.toLocaleString('nl-NL')}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">{formatEUR(r.cost_eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 text-xs text-blue-900 flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5" />
            <div>
              <p className="font-semibold mb-1">Bescherming tegen onbedoeld misbruik</p>
              <p>Per locatie: max 5 AI-generate calls per dag (in-memory teller) + maandelijkse budget-cap (configureerbaar in Instellingen). Bij 80% van de cap krijgt de bedrijfsleider een waarschuwing; bij 100% wordt de AI tijdelijk uitgeschakeld en kan de fallback-rollout van het standaardrooster worden gebruikt.</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Card({ icon: Icon, label, value, sublabel }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
          <p className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
          {sublabel && <p className="mt-1 text-xs text-slate-500">{sublabel}</p>}
        </div>
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-amber/15 text-brand-amber">
          <Icon size={16} />
        </div>
      </div>
    </div>
  );
}
